/**
 * 任務條幾何計算：純函式，不觸碰 DOM，方便單元測試。
 */

import { parseDateTime, timeToX, type TimeAxis } from './time-scale.js';
import type { GanttTask } from './types.js';

/** 任務條種類，決定繪製分支。 */
export type BarKind = 'normal' | 'summary' | 'milestone';

export interface BarGeometry {
  readonly kind: BarKind;
  /** 條左緣（milestone 為菱形中心）。 */
  readonly x: number;
  /** 條寬度（milestone 為 0）。 */
  readonly width: number;
  readonly y: number;
  readonly height: number;
  /** 列的垂直中心。 */
  readonly midY: number;
  /** 進度條寬度。 */
  readonly progressWidth: number;
  /** 條右緣（milestone 為中心）。 */
  readonly endX: number;
}

export interface BaselineGeometry {
  readonly x: number;
  readonly width: number;
  readonly y: number;
  readonly height: number;
}

/** 里程碑菱形的半徑（px）。 */
export const MILESTONE_RADIUS = 5;
/** 摘要括號兩端尖角的水平寬度。 */
export const SUMMARY_TIP_WIDTH = 5;
/** 基準條高度。 */
export const BASELINE_HEIGHT = 2;
/** 基準條與任務條之間的間距。 */
export const BASELINE_GAP = 1;
/** 一般條的最小寬度，避免極短任務消失。 */
export const MIN_BAR_WIDTH = 2;
/** 顯示基準線時列底部要保留的空間。 */
const BASELINE_RESERVE = BASELINE_GAP + BASELINE_HEIGHT;

/**
 * 把條的上緣夾在列內。
 *
 * 兩個不變量：
 * 1. 永遠不高於列的上緣（`rowIndex = 0` 時即不得為負）。
 * 2. 列還放得下時，整段繪製範圍（含摘要尖角、基準條）留在列內。
 *
 * 列高小於繪製範圍時以「不越過上緣」為優先，讓它往下溢出而非畫到別人的列上。
 */
function clampTop(
  desired: number,
  rowTop: number,
  rowHeight: number,
  extent: number,
): number {
  const lowest = rowTop + Math.max(0, rowHeight - extent);
  return Math.max(rowTop, Math.min(desired, lowest));
}

/** 里程碑菱形半徑：列很矮時跟著縮小，不撐破列高。 */
export const milestoneRadiusFor = (rowHeight: number): number =>
  Math.max(2, Math.min(MILESTONE_RADIUS, Math.floor(rowHeight / 2)));

export const barKindOf = (task: GanttTask): BarKind =>
  task.isMilestone ? 'milestone' : task.isSummary ? 'summary' : 'normal';

/**
 * 依列高推導條的高度。
 *
 * MS Project 的視覺節奏靠「細條 + 大留白」：一般條約列高的 1/3，
 * 摘要則只是一條 3–5px 的細線（真正的體積來自兩端往下折的尖角）。
 */
export function barHeightFor(kind: BarKind, rowHeight: number): number {
  if (kind === 'summary') return Math.max(2, Math.min(3, Math.round(rowHeight * 0.09)));
  return Math.max(6, Math.min(14, Math.round(rowHeight * 0.34)));
}

/** 摘要括號尖角往下延伸的長度（由線粗推導，維持比例）。 */
export const summaryTipHeight = (lineHeight: number): number =>
  Math.max(3, Math.round(lineHeight * 2));

/** 摘要名稱標在括號上方所需的行高。 */
const SUMMARY_LABEL_ROOM = 12;

export interface RowLayoutOptions {
  readonly rowHeight: number;
  readonly showBaseline: boolean;
}

/** 計算某一列（rowIndex）任務條的座標。 */
export function barGeometry(
  task: GanttTask,
  rowIndex: number,
  axis: TimeAxis,
  options: RowLayoutOptions,
): BarGeometry {
  const { rowHeight, showBaseline } = options;
  const kind = barKindOf(task);
  const startMs = parseDateTime(task.start);
  const finishMs = parseDateTime(task.finish);
  const rawX = timeToX(axis, startMs);
  const rawEnd = timeToX(axis, finishMs);

  const usableHeight = Math.max(1, rowHeight);
  const rowTop = rowIndex * usableHeight;
  const midY = rowTop + usableHeight / 2;
  // 有基準線時整體上移，讓下方留出細灰條的空間。
  const reserve = showBaseline ? BASELINE_RESERVE : 0;
  const centerShift = Math.round(reserve / 2);

  if (kind === 'milestone') {
    const radius = milestoneRadiusFor(usableHeight);
    const size = radius * 2;
    return {
      kind,
      x: rawX,
      width: 0,
      y: clampTop(midY - radius - centerShift, rowTop, usableHeight, size + reserve),
      height: size,
      midY,
      progressWidth: 0,
      endX: rawX,
    };
  }

  const height = Math.max(1, Math.min(barHeightFor(kind, usableHeight), usableHeight));

  // 摘要括號的線落在列中線下方：上方讓給任務名稱，下方剛好容納尖角。
  const y =
    kind === 'summary'
      ? clampTop(
          Math.max(rowTop + SUMMARY_LABEL_ROOM, Math.round(midY)),
          rowTop,
          usableHeight,
          height + summaryTipHeight(height) + reserve,
        )
      : clampTop(midY - height / 2 - centerShift, rowTop, usableHeight, height + reserve);

  const width = Math.max(MIN_BAR_WIDTH, rawEnd - rawX);
  const percent = Math.min(100, Math.max(0, task.percentComplete as unknown as number));
  return {
    kind,
    x: rawX,
    width,
    y,
    height,
    midY,
    progressWidth: (width * percent) / 100,
    endX: rawX + width,
  };
}

/** 條實際佔用的垂直範圍（摘要含尖角、里程碑含菱形）。 */
export interface BarBounds {
  readonly top: number;
  readonly bottom: number;
}

export function barBounds(bar: BarGeometry): BarBounds {
  const extra = bar.kind === 'summary' ? summaryTipHeight(bar.height) : 0;
  return { top: bar.y, bottom: bar.y + bar.height + extra };
}

/** 條的左右實際範圍（里程碑以菱形半徑外擴）。 */
export function barSpan(bar: BarGeometry): { readonly left: number; readonly right: number } {
  if (bar.kind === 'milestone') {
    // 里程碑的半徑已編碼在 height（= 2r），依列高縮放時仍然一致。
    const radius = bar.height / 2;
    return { left: bar.x - radius, right: bar.x + radius };
  }
  return { left: bar.x, right: bar.endX };
}

/**
 * 進度線：自條的左緣延伸到完成百分比位置的深色細線，垂直置中。
 *
 * 線的「長度」本身就是進度，因此刻意做細（不做成色塊），避免讀成污漬。
 */
export function progressBand(bar: BarGeometry): { readonly y: number; readonly height: number } {
  const height = Math.max(2, Math.round(bar.height * 0.26));
  return { y: Math.round(bar.y + bar.height / 2 - height / 2), height };
}

/** 進度握把命中區比進度細線上下各多出的高度上限。 */
export const PROGRESS_HIT_PADDING = 4;

/**
 * 進度握把的命中區（僅垂直範圍；水平範圍在 TaskBar 以進度線右緣為中心取 6px）。
 *
 * 進度細線只有 2–4px，直接拿它當命中區會按不到；但也不能整條都算進度，
 * 否則會與「拖曳條身 = 移動任務」搶同一塊區域。
 *
 * 契約（由 geometry 測試釘住）：
 * - 上下緣一律是整數像素（順帶避免 SVG 半像素模糊）。
 * - 與條的上緣、下緣**各至少相距 1px**，那 1px 永遠屬於「移動任務」。
 * - 條矮到連上述保證都放不下時回傳 `height = 0`（不產生命中區），
 *   整條讓給移動任務，而不是犧牲保證。
 */
export function progressHitArea(
  bar: BarGeometry,
  band: { readonly y: number; readonly height: number },
): { readonly y: number; readonly height: number } {
  const limitTop = Math.ceil(bar.y + 1);
  const limitBottom = Math.floor(bar.y + bar.height - 1);
  if (limitBottom - limitTop < 1) return { y: limitTop, height: 0 };

  const top = Math.min(
    Math.max(Math.floor(band.y) - PROGRESS_HIT_PADDING, limitTop),
    limitBottom - 1,
  );
  const bottom = Math.max(
    Math.min(Math.ceil(band.y + band.height) + PROGRESS_HIT_PADDING, limitBottom),
    top + 1,
  );
  return { y: top, height: bottom - top };
}

/** 基準條座標；無基準資料時回傳 null。 */
export function baselineGeometry(
  task: GanttTask,
  bar: BarGeometry,
  axis: TimeAxis,
): BaselineGeometry | null {
  if (task.baselineStart === null || task.baselineFinish === null) return null;
  const x = timeToX(axis, parseDateTime(task.baselineStart));
  const endX = timeToX(axis, parseDateTime(task.baselineFinish));
  return {
    x,
    width: Math.max(MIN_BAR_WIDTH, endX - x),
    y: barBounds(bar).bottom + BASELINE_GAP,
    height: BASELINE_HEIGHT,
  };
}

/** 里程碑菱形的 SVG points 字串。 */
export function milestonePoints(bar: BarGeometry): string {
  const cx = bar.x;
  const r = bar.height / 2;
  const cy = bar.y + r;
  return `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
}

/**
 * MS Project 樣式的摘要「括號」。
 *
 * 一條細的水平線，兩端各往下折出一個尖角；中間不填色，
 * 因此摘要不會與底下的子任務搶注意力。
 */
export function summaryBarPath(bar: BarGeometry): string {
  const { x, y, width } = bar;
  const body = Math.max(2, bar.height);
  const tip = Math.min(SUMMARY_TIP_WIDTH, width / 2);
  const bottom = y + body + summaryTipHeight(body);
  return [
    `M ${x} ${y}`,
    `L ${x + width} ${y}`,
    `L ${x + width} ${bottom}`,
    `L ${x + width - tip} ${y + body}`,
    `L ${x + tip} ${y + body}`,
    `L ${x} ${bottom}`,
    'Z',
  ].join(' ');
}

/** 任務是否落在可視的水平像素範圍內（橫向虛擬化）。 */
export function isBarVisible(
  bar: BarGeometry,
  viewLeft: number,
  viewRight: number,
): boolean {
  const { left, right } = barSpan(bar);
  // 右側還有任務名稱／資源文字，多給一些寬容值。
  return right + 160 >= viewLeft && left <= viewRight;
}
